/**
 * 지출결재 3종 문서 API 클라이언트 — backend /expense/* 와 1:1 (v2.50.0).
 * 구입지급결의서 · 개인경비정산서 · 법인카드사용내역서 + 법인카드 사용내역 업로드.
 */

import { apiErrorText } from './api-error-text';
import type { ApiResponse } from './types';
import type { HolidayDetail } from './holiday-api';
import type { MeetingDetail } from './meeting-api';
import type { TspReportDetail } from './tsp-report-api';
import type { SalesReportBody } from './sales-report-api';
import type { StockAuditReportBody } from './stock-audit-api';
import type { DeptReportBody } from './dept-report-api';

const BASE = '/api';

export type ExpenseDocType = '구입지급결의서' | '개인경비정산서' | '법인카드사용내역서';
/**
 * 같은 결재선을 도는 사내 문서 전부 (v2.97.0 · 기술지원팀월간보고서는 v2.113.0 ·
 * 영업팀월간보고서는 v2.115.0).
 * 휴일근무일지·기술지원팀월간보고서·영업팀월간보고서는 **지출이 아니라** 종류 필터·통계에는
 * 안 나오지만, 목록·상세·결재함은 같은 통로를 쓴다.
 *
 * ⚠종류가 늘 때마다 「빼기 판정」(이 종류만 빼고 전부)이 샌다 — 새 종류를 여기 넣었으면
 *   이 union 을 훑는 화면이 **더하기(허용 목록)**로 적혀 있는지 함께 볼 것.
 */
export type ApprovalDocType =
  | ExpenseDocType
  | '휴일근무일지'
  | '기술지원팀월간보고서'
  | '영업팀월간보고서'
  | '회의록'
  // 분기 재고 실사 보고서 (v2.224.0) — 앱의 분기 실사 화면에서 상신. 결재선 3칸
  | '분기재고실사보고서'
  // 부서별 월간보고서 (v2.280.0) — 경영지원부·기업부설연구소가 사내결재 문서함에서 쓴다. 결재선 3칸
  | '부서월간보고서';
export type ExpenseStatus = '작성중' | '검토대기' | '부서장승인' | '경영지원승인' | '최종승인' | '반려';

export const EXPENSE_DOC_TYPES: ExpenseDocType[] = [
  '구입지급결의서', '개인경비정산서', '법인카드사용내역서',
];

/** 계정과목 9종 고정 (2026-08-04 확정 — 샘플 양식 기준) */
export const EXPENSE_ACCOUNTS = [
  '복리후생비', '차량유지비', '접대비', '여비교통비',
  '소모품비', '운반비', '통신비', '사무용품비', '기타',
] as const;
export type ExpenseAccount = (typeof EXPENSE_ACCOUNTS)[number];

/** 내용 키워드 → 계정과목 자동추천 (샘플 양식의 자동입력 규칙 그대로) */
export const ACCOUNT_KEYWORDS: [ExpenseAccount, string[]][] = [
  ['복리후생비', ['회식', '식대', '음료', '차대', '간식']],
  ['차량유지비', ['충전', '주유', '유대', '주차비', '차량수리', '세차', '엔진오일', '정비']],
  ['접대비', ['접대', '식사접대', '거래처접대']],
  ['여비교통비', ['통행료', '대리', '하이패스', '택시', '숙박', '항공권', '철도승차권', '고속버스', '대중교통']],
  ['소모품비', ['소모품', '소모품구입']],
  ['운반비', ['퀵서비스', '운반비']],
  ['통신비', ['우편', '등기']],
  ['사무용품비', ['사무용품', '사무용품구입']],
  // ⭐v2.60.0 — 맨 뒤에 둔다. suggestAccount 는 **먼저 걸린 것**을 쓰므로,
  //   위 과목들이 아무것도 안 걸렸을 때만 「기타」로 간다.
  ['기타', ['AI구독']],
];

export function suggestAccount(description: string): ExpenseAccount | null {
  const d = (description ?? '').trim();
  if (!d) return null;
  for (const [account, words] of ACCOUNT_KEYWORDS) {
    if (words.some((w) => d.includes(w))) return account;
  }
  return null;
}

/**
 * 보조과목 (법인카드 전용, v2.51.0) — 샘플 양식의 계정과목 분류 그대로 +
 * 모든 계정과목에 '기타'. 백엔드 lib/approval/expense.ts 와 같은 목록.
 *
 * ⭐v2.58.0 — 차량유지비에 **충전** 추가 (전기차 충전용).
 * ⭐v2.59.0 — 종이 양식 안내표에 있던 것을 빠짐없이 맞춤 (간식·엔진오일·정비·소모품·사무용품).
 *   ⚠백엔드 lib/approval/expense.ts 와 **반드시 같이** 고쳐야 한다 — 서버가 isValidSubAccount 로 거른다.
 */
export const EXPENSE_SUB_ACCOUNTS: Record<string, string[]> = {
  복리후생비: ['회식', '식대', '음료', '차대', '간식', '기타'],
  차량유지비: ['충전', '주유', '유대', '주차비', '차량수리', '세차', '엔진오일', '정비', '기타'],
  접대비: ['접대', '식사접대', '거래처접대', '기타'],
  여비교통비: ['통행료', '대리', '하이패스', '택시', '숙박', '항공권', '철도승차권', '고속버스', '대중교통', '기타'],
  소모품비: ['소모품', '소모품구입', '기타'],
  운반비: ['퀵서비스', '운반비', '기타'],
  통신비: ['우편', '등기', '기타'],
  사무용품비: ['사무용품', '사무용품구입', '기타'],
  // ⭐v2.60.0 — 「기타」에 AI구독 추가. 백엔드 lib/approval/expense.ts 와 반드시 같이 고칠 것.
  기타: ['AI구독', '기타'],
};

/**
 * 보조과목 검색 목록 (v2.93.0) — 「보조과목을 치면 계정과목이 따라온다」의 원천.
 *
 * ⭐**보조과목 하나가 아니라 (보조과목·계정과목) 한 쌍**이 목록의 한 줄이다.
 *   「기타」가 9개 계정과목 모두에 있어서, 보조과목 이름만으로는 계정과목이 안 정해진다.
 *   쌍으로 두면 「기타」는 9줄로 갈라져 고르는 순간 계정과목이 확정된다.
 *   나머지 42개는 이름이 유일해 한 줄만 뜬다(=사실상 바로 확정).
 * ⚠목록을 손으로 또 적지 않는다 — EXPENSE_SUB_ACCOUNTS 에서 만들어 쓴다(단일 원천).
 */
export interface SubAccountOption { sub: string; account: ExpenseAccount }

export const SUB_ACCOUNT_OPTIONS: SubAccountOption[] = EXPENSE_ACCOUNTS.flatMap((a) =>
  (EXPENSE_SUB_ACCOUNTS[a] ?? []).map((s) => ({ sub: s, account: a })),
);

/** 그 보조과목 이름을 쓰는 계정과목이 몇 개인가 — 1개면 계정과목이 자동으로 정해진다 */
export function accountsForSub(sub: string): ExpenseAccount[] {
  return SUB_ACCOUNT_OPTIONS.filter((o) => o.sub === sub).map((o) => o.account);
}

/** 검색 — 보조과목·계정과목 어느 쪽 글자로도 찾는다. 빈 검색어는 전체 */
export function searchSubAccounts(q: string): SubAccountOption[] {
  const t = (q ?? '').trim();
  if (t === '') return SUB_ACCOUNT_OPTIONS;
  return SUB_ACCOUNT_OPTIONS.filter((o) => o.sub.includes(t) || o.account.includes(t));
}

/**
 * 사용처 글자 → 보조과목 자동 (v2.93.0 · 2026-08-25 사용자 지시).
 *
 * 카드사 내역의 사용처는 고칠 수 없는 원본이라, 거기 「주유소」가 들어 있으면
 * 주유(차량유지비)로 미리 채워 둔다. ⚠**기본값일 뿐** — 실제로 같은 「구광주유소」에서
 * 회식(복리후생비)으로 처리한 건도 있어(운영 실측) 반드시 고칠 수 있어야 한다.
 */
export const VENDOR_SUB_RULES: { keyword: string; sub: string; account: ExpenseAccount }[] = [
  { keyword: '주유소', sub: '주유', account: '차량유지비' },
  // ⭐v2.227.0 (2026-10-02 사용자 지시) — 「주유·에너지·석유」. 운영 1~9월 실측: 「주유」 든 사용처 12곳·「석유」 6곳·
  //   「에너지」 6곳(케이에너지·효창에너지 …) 전부 주유소였다(다르게 적힌 건 주유소에서 산 세차·유대뿐).
  { keyword: '주유', sub: '주유', account: '차량유지비' },
  { keyword: '석유', sub: '주유', account: '차량유지비' },
  { keyword: '구도일', sub: '주유', account: '차량유지비' },   // S-OIL 주유소 이름(태영구도일 등) — 3/3
  // ⭐v2.110.0 (2026-09-01 사용자 지시) — 전기차 충전. 운영 실적이 정답지다:
  //   휴맥스이브이 8건 · 플러그링크 전기차충 4건 · 전기차충전_1 1건을 사람이 전부
  //   「차량유지비/충전」으로 분류했다. 그 손을 덜어 준다.
  // ⚠ 「EV」는 넣지 않는다 — 위베이브(WEVAVE) 같은 식당·접대 건이 딸려온다(운영 실측).
  // ⚠ 앞의 규칙이 먼저 이긴다 — 이름에 「주유소」가 함께 들어 있으면 예전대로 주유.
  { keyword: '휴맥스이브이', sub: '충전', account: '차량유지비' },
  { keyword: '전기차', sub: '충전', account: '차량유지비' },
  { keyword: '충전', sub: '충전', account: '차량유지비' },
  // ⚠「에너지」는 충전 규칙 **뒤**에 둔다 — 「○○에너지 전기차충전」 같은 이름이면 충전이 먼저 이기게(운영엔 아직 없음).
  { keyword: '에너지', sub: '주유', account: '차량유지비' },
  // ⭐v2.228.0 (2026-10-02 사용자 「사하구청은 주차비로 많이 올렸는데」) — 주차. 운영 1~9월 실측 「주차」 6줄·「파킹」 36줄·
  //   「시설관리」 23줄 전부 주차비. 충전 규칙 뒤라 「○○파킹 전기차충전」이면 충전이 이긴다.
  //   ⛔「구청」은 넣지 않는다 — 74줄 중 20줄이 구청 근처 카페·식당(바나프레소 도봉구청 등). 구청 주차는 지난 기록 규칙 몫.
  { keyword: '주차', sub: '주차비', account: '차량유지비' },
  { keyword: '파킹', sub: '주차비', account: '차량유지비' },
  { keyword: '시설관리', sub: '주차비', account: '차량유지비' },
  // ⭐v2.227.0 — 음료. 사용자 결정: 스타벅스·카페도 음료(운영 실측 절반쯤 접대비 — 「자동」 표시를 보고 작성자가 고친다) ·
  //   커피를 「차대」로 적던 기록이 있어도 음료로 채운다(「음료/차대 모두 음료로」).
  { keyword: '스타벅스', sub: '음료', account: '복리후생비' },
  { keyword: '바나프레소', sub: '음료', account: '복리후생비' },
  { keyword: '빽다방', sub: '음료', account: '복리후생비' },
  { keyword: '카페', sub: '음료', account: '복리후생비' },
  { keyword: '로스터리락온', sub: '음료', account: '복리후생비' },
  { keyword: '메가엠지씨', sub: '음료', account: '복리후생비' },   // 메가커피 — 12/14(나머지 차대)
  { keyword: '메가커피', sub: '음료', account: '복리후생비' },     // 같은 가게의 다른 표기
  // ⭐v2.227.0 — 식대. 요청 7개 + 조사로 더한 9개(운영 실측 61줄 중 60줄이 식대 · 순대 1줄만 접대).
  { keyword: '돈까스', sub: '식대', account: '복리후생비' },
  { keyword: '스시', sub: '식대', account: '복리후생비' },
  { keyword: '버거', sub: '식대', account: '복리후생비' },
  { keyword: '김가네', sub: '식대', account: '복리후생비' },
  { keyword: '김밥', sub: '식대', account: '복리후생비' },
  { keyword: '비빔밥', sub: '식대', account: '복리후생비' },
  { keyword: '맥도날드', sub: '식대', account: '복리후생비' },
  { keyword: '샐러디', sub: '식대', account: '복리후생비' },
  { keyword: '써브웨이', sub: '식대', account: '복리후생비' },
  { keyword: '우동', sub: '식대', account: '복리후생비' },
  { keyword: '순대', sub: '식대', account: '복리후생비' },
  { keyword: '해장국', sub: '식대', account: '복리후생비' },
  { keyword: '초밥', sub: '식대', account: '복리후생비' },
  { keyword: 'KFC', sub: '식대', account: '복리후생비' },
  { keyword: '맘스터치', sub: '식대', account: '복리후생비' },
  { keyword: '국밥', sub: '식대', account: '복리후생비' },
];

/**
 * 같은 사용처의 지난 기록으로 채울 값 (v2.227.0) — 서버가 문서 상세에 실어 준다(cardVendorHints).
 * 규칙(모든 보조과목 · 3번 이상 · 가장 많이 기록된 분류 · 차대=음료)은 서버 lib/approval/card-vendor-hint.ts 한 곳.
 */
export interface VendorHint { account: string; sub: string; count: number; total: number }

/** 자동으로 정한 보조과목 + 왜 그렇게 정했나(「자동」 표시의 말풍선) */
export interface VendorSuggestion extends SubAccountOption { why: string }

/**
 * 사용처 → 보조과목 기본값. ① 사용처 글자 규칙(위 목록 · 앞이 먼저) ② 같은 사용처의 지난 기록(서버 hint).
 * ⚠기본값일 뿐 — 화면이 「자동」 표시를 붙이고, 사람이 고르면 그 값이 이긴다.
 */
export function suggestSubByVendor(
  vendor: string | null | undefined, hint?: VendorHint | null,
): VendorSuggestion | null {
  const v = (vendor ?? '').trim();
  if (v === '') return null;
  const hit = VENDOR_SUB_RULES.find((r) => v.includes(r.keyword));
  if (hit) return { sub: hit.sub, account: hit.account, why: `사용처 글자 「${hit.keyword}」 → ${hit.sub}` };
  if (hint && (EXPENSE_SUB_ACCOUNTS[hint.account] ?? []).includes(hint.sub)) {
    return {
      sub: hint.sub, account: hint.account as ExpenseAccount,
      why: `이 사용처는 지난 기록 ${hint.total}번 중 ${hint.count}번이 ${hint.sub}`,
    };
  }
  return null;
}

/** 사용대상 입력 방식 (법인카드) — 2026-08-04 사용자 확정 */
export type UsageTargetMode = 'employees' | 'text' | 'none';
export function usageTargetMode(account: string | null | undefined): UsageTargetMode {
  if (account === '복리후생비') return 'employees'; // 직원 체크박스
  if (account === '차량유지비' || account === '접대비' || account === '여비교통비') return 'text';
  return 'none'; // 그 외 — 사용대상 생략, 바로 적요
}
export const USAGE_TARGET_PLACEHOLDER: Record<string, string> = {
  차량유지비: '차량번호',
  접대비: '거래처 이름',
  여비교통비: '목적지',
};

export interface ExpenseItem {
  itemDate?: string | null;
  account?: ExpenseAccount | null;
  /** 보조과목 (법인카드) */
  subAccount?: string | null;
  /** 보조과목을 포털이 자동으로 채웠고 사람이 아직 안 바꿨다 (법인카드 · v2.227.0) — 「자동」 표시 */
  subAuto?: boolean;
  /** 사용대상 (법인카드 — 생략 가능) */
  usageTarget?: string | null;
  vendor?: string | null;
  description?: string | null;
  qty?: number | null;
  unitPrice?: number | null;
  amount: number;
  note?: string | null;
  /** 판매물품 결의서 줄의 영업건 (v2.246.0) — 비면 재고 보유. 최종승인 때 이대로 매입이 만들어진다 */
  salesId?: string | null;
  /**
   * 판매물품 결의서 줄의 매입 품목 (v2.304.0 · 개발요청 #16) — 기존 품목(itemCode) 또는 목록에 없는 품목(그룹·품명·규격).
   *   최종승인 때 매입 품목으로 들어가고, 품목이 있으면 매입관리에 「확인필요」 없이 올라간다 · 제출 때는 꼭 있어야 한다.
   */
  itemCode?: string | null;
  newItemGroup?: string | null;
  newItemName?: string | null;
  newItemSpec?: string | null;
}

/**
 * 구입지급결의서 「지출 분류」 5종 (v2.132.0).
 * ⚠ 백엔드 `lib/approval/expense.ts` 의 SPEND_CATEGORIES · DB CHECK 와 **같은 순서·같은 글자**.
 *   비품 및 소모품·마케팅 = 통계용 · 판매물품 = 매입관리 연동 · 수출입관련 = 수출입 비용 연동.
 */
export const SPEND_CATEGORIES = ['비품 및 소모품', '마케팅', '판매물품', '수출입관련', '기타'] as const;
export type SpendCategory = (typeof SPEND_CATEGORIES)[number];

export interface ExpenseDocFields {
  docDate?: string;
  purchaseType?: string | null;
  spendCategory?: SpendCategory | null;
  purpose?: string | null;
  vendor?: string | null;
  payMethod?: string | null;
  /** 옛 「결제날짜」 글 — v2.267.0 부터 입력칸은 없다. 옛 문서를 고칠 때 받은 값을 그대로 돌려보낸다(서버가 지우지 않게) */
  payDateText?: string | null;
  /** 지급일 (v2.267.0) — 구입지급결의서·개인경비정산서 · null = 비움 · 안 보내면 서버가 그대로 둔다 */
  payDate?: string | null;
  vatAmount?: number;
  bankHolder?: string | null;
  bankName?: string | null;
  bankAccount?: string | null;
  cardNo?: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  items: ExpenseItem[];
  /** 수출입관련 결의서의 수출입건(오더) 번호들 — 여러 건 (v2.230.0 · 개발 요청 #14) */
  importOrderIds?: number[];
  submit?: boolean;
}

export interface ExpenseDocListRow {
  id: number;
  /** ⚠결재함(group=all)은 휴일근무일지도 함께 받는다 — 지출 3종으로 좁히면 안 된다 */
  docType: ApprovalDocType;
  docNo: string;
  authorId: string;
  authorName: string;
  authorDept: string | null;
  docDate: string;
  status: ExpenseStatus;
  totalAmount: number;
  vendor: string | null;
  purpose: string | null;
  cardNo: string | null;
  submittedAt: string | null;
  updatedAt: string;
  rejectReason: string | null;
  /** 소급 등록 안내 (v2.57.0). null = 포털에서 결재가 돈 일반 문서 */
  backfillNote: string | null;
  accounts: string[];
  /** 지출 분류 (v2.132.0) — 구입지급결의서에만 있다 */
  spendCategory: SpendCategory | null;
  /**
   * 💸 지급일·송금 (v2.267.0) — 구입지급결의서·개인경비정산서.
   *   payDate = 작성자 지급일 · payDateText = 옛 「결제날짜」 글 · remitDate null = 송금 전 ·
   *   remitLegacy = 이 기능 전에 이미 최종승인된 「이전 문서」(송금 대기로 세지 않는다).
   * ⚠옛 서버(배포 도중)는 이 칸들을 안 준다 → optional.
   */
  payDate?: string | null;
  payDateText?: string | null;
  /** 보여 줄 지급일 글 — 날짜 + 옛 결제날짜 글을 서버가 이어 준다 (v2.268.0 · PDF·대장과 같은 함수) */
  payDateShown?: string;
  remitDate?: string | null;
  remitLegacy?: boolean;
  remitByName?: string | null;
  /** 💸 송금 상태 — 서버가 정한다 (v2.268.0) · 옛 서버는 안 준다 */
  remitState?: RemitState;
  attachmentId: number | null;
  /**
   * 휴일근무일지에 **내가 근무자로 올라 있는가** (v2.102.1).
   * 화면이 「내 문서」를 고를 때 작성자만 보면 함께 근무한 사람의 문서가 사라진다.
   */
  iAmWorker: boolean;
  /**
   * 회의록에 **내가 참석자로 올라 있는가** (v2.118.0). 위와 같은 이유로 칸을 따로 둔다 —
   * 이름이 「근무자」인 칸에 회의 참석자를 섞으면 읽는 곳이 무슨 뜻인지 못 읽는다.
   */
  iAmAttendee: boolean;
  /**
   * 회의록 동의 서명 현황 (v2.229.0) — 회의록이 아니면 null. ⚠옛 서버(배포 도중)는 이 칸을 안 준다 → optional.
   *   required = 서명할 사람 수 · signed = 서명한 사람 수 · openRequests = 「반영함」 안 된 변경 요청 ·
   *   iMustSign = 내 서명을 기다린다 · legacy = 결재로 끝난 옛 회의록(결재판 그대로)
   */
  meetingSign?: MeetingSignSummary | null;
  /**
   * 「몇 월분 자료인가」 (v2.62.0) — 'YYYY-MM'. **작성일이 아니다.**
   * 6월분 카드정산서를 7/2 에 작성하면 작성일은 07-02 지만 귀속월은 06 이다.
   * from 과 to 가 같으면 한 달짜리. 판정 근거는 attributedBasis 가 알려 준다.
   */
  attributedFrom: string;
  attributedTo: string;
  /** 무엇으로 뽑았나 — 사용기간(카드) · 사용일(경비) · 작성일(구입지급) */
  attributedBasis: '사용기간' | '사용일' | '작성일';
}

/** 'YYYY-MM' → 「2026년 7월분」 · 여러 달이면 「2026년 5~6월분」 (2026-08-07 사용자 확정 표기) */
export function attributedMonthLabel(from: string, to: string): string {
  const [fy, fm] = from.split('-');
  const [ty, tm] = to.split('-');
  if (!fy || !fm) return from;
  if (from === to) return `${fy}년 ${Number(fm)}월분`;
  if (fy === ty) return `${fy}년 ${Number(fm)}~${Number(tm)}월분`;
  return `${fy}년 ${Number(fm)}월 ~ ${ty}년 ${Number(tm)}월분`;
}

/** 회의록 목록 한 줄의 서명 현황 (v2.229.0) */
export interface MeetingSignSummary {
  required: number;
  signed: number;
  openRequests: number;
  iMustSign: boolean;
  legacy: boolean;
}

/** 서명할 사람 한 명 (작성자를 뺀 사내 참석자) — 서버 lib/approval/meeting-sign.ts MeetingSigner 와 같은 모양 */
export interface MeetingSigner {
  userId: string;
  name: string;
  title: string | null;
  department: string | null;
  /** 계정이 살아 있나 — 퇴사(비활성) 계정은 서명을 기다리지 않는다 */
  active: boolean;
  hasSignature: boolean;
  signedAt: string | null;
  stale: boolean;
}

/** 변경 요청(참석자) · 답글(작성자) 한 줄 */
export interface MeetingComment {
  id: number;
  writerId: string;
  writerName: string;
  isRequest: boolean;
  body: string;
  createdAt: string;
  resolvedAt: string | null;
  resolvedByName: string | null;
  canResolve: boolean;
  canDelete: boolean;
}

/** 회의록 동의 서명 — 상세 (v2.229.0) */
export interface MeetingSignDetail {
  /** 결재로 끝난 옛 회의록 — 서명·고치기·댓글이 없다(결재판 그대로) */
  legacy: boolean;
  /** 지금 내용의 지문 — 서명할 때 그대로 돌려보낸다(그 사이 내용이 바뀌면 서버가 거절한다) */
  hash: string;
  required: number;
  signed: number;
  complete: boolean;
  author: { userId: string; name: string; hasSignature: boolean; at: string | null };
  signers: MeetingSigner[];
  canSign: boolean;
  canUnsign: boolean;
  canComment: boolean;
  /** 서명 완료된 회의록을 다시 고칠 수 있나(작성자) — 고치면 서명을 다시 받는다 */
  canReopen: boolean;
  comments: MeetingComment[];
}

export interface ExpenseLineCell {
  role: '담당' | '부서장' | '경영지원' | '대표이사';
  userId: string | null;
  name: string;
  hasSignature: boolean;
  stamped: boolean;
  stampedAt: string | null;
  skipped: boolean;
  /** 대리결재로 찍힌 칸 — 「대결」 표기 (v2.100.0) */
  delegated?: boolean;
}

/** 수출입건 표기 — 결의서에 걸린 것(고른 때의 글자) · 고르기 칸 찾기 결과 (v2.230.0) */
export interface ImportOrderTag {
  /** 오더가 지워졌으면 null (표기는 남는다) */
  importOrderId: number | null;
  refNo: string;
  /** 「GS2016R 컨트롤러 외 1건 · 국토관리청, 종로구청」 */
  summary: string;
}
export interface ImportOrderPick extends ImportOrderTag {
  importOrderId: number;
  isRma: boolean;
  orderDate: string | null;
}

/** 수출입건 고르기 칸 찾기 — 오더번호·품목·엔드유저·영업번호 (빈 글이면 최근 30건 · 금액 없음) */
export async function searchImportOrders(q: string): Promise<ImportOrderPick[]> {
  const qs = new URLSearchParams();
  if (q.trim()) qs.set('q', q.trim());
  return call(`${BASE}/expense/import-orders${qs.toString() ? `?${qs.toString()}` : ''}`);
}

export interface ExpenseDocDetail {
  /** 수출입관련 결의서의 수출입건 (v2.230.0) — 다른 문서는 빈 배열 */
  importOrders?: ImportOrderTag[];
  doc: {
    id: number;
    /** ⚠휴일근무일지도 이 상세 통로를 쓴다 */
    docType: ApprovalDocType;
    docNo: string;
    authorId: string;
    authorName: string;
    authorDept: string | null;
    authorTitle: string | null;
    docDate: string;
    status: ExpenseStatus;
    purchaseType: string | null;
    spendCategory: SpendCategory | null;
    purpose: string | null;
    vendor: string | null;
    payMethod: string | null;
    payDateText: string | null;
    /** 💸 v2.267.0 — 지급일(작성자) · 송금일(경영지원 · null = 송금 전) · 「이전 문서」. ⚠옛 서버는 안 준다 → optional */
    payDate?: string | null;
    /** 보여 줄 지급일 글 — 서버가 날짜 + 옛 결제날짜 글을 이어 준다 (v2.268.0) */
    payDateShown?: string;
    remitDate?: string | null;
    remitAt?: string | null;
    remitLegacy?: boolean;
    /** 💸 송금 상태 — 서버가 정한다 (v2.268.0) */
    remitState?: RemitState;
    vatAmount: number;
    bankHolder: string | null;
    bankName: string | null;
    bankAccount: string | null;
    cardNo: string | null;
    periodFrom: string | null;
    periodTo: string | null;
    statementId: number | null;
    totalAmount: number;
    submittedAt: string | null;
    rejectedBy: string | null;
    rejectedAt: string | null;
    rejectReason: string | null;
    /** 소급 등록 안내 (v2.57.0). null = 포털에서 결재가 돈 일반 문서 */
    backfillNote: string | null;
    updatedAt: string;
  };
  /**
   * 휴일근무일지 본문 (v2.97.0) — docType 이 '휴일근무일지' 일 때만 채워진다.
   * 결재판(cells)과 **한 번에** 온다 — 나눠 부르면 둘이 어긋난 순간이 화면에 보인다.
   */
  holiday: HolidayDetail | null;
  /**
   * 기술지원팀 월간보고서 본문 (v2.113.0) — docType 이 '기술지원팀월간보고서' 일 때만 채워진다.
   * 사람이 쓴 문서가 아니라 기술지원 포털이 API 로 보내 온 문서라, 「어디서 온 무엇인가」를
   * 화면이 보여 줄 수 있어야 한다.
   */
  tspReport: TspReportDetail | null;
  /**
   * 영업팀 월간보고서 본문 (v2.115.0) — docType 이 '영업팀월간보고서' 일 때만 채워진다.
   * ⚠**보고서 숫자는 여기 없다** — 상신 때 굳은 집계는 확정본 PDF 첨부 안에만 있다.
   *   화면이 숫자를 다시 그리면 「지금 값」이라 결재된 문서와 어긋난다.
   */
  salesReport: SalesReportBody | null;
  /**
   * 회의록 본문 (v2.118.0) — docType 이 '회의록' 일 때만 채워진다.
   * 결재판(cells)과 **한 번에** 온다 — 나눠 부르면 둘이 어긋난 순간이 화면에 보인다.
   */
  meeting: MeetingDetail | null;
  /**
   * 회의록 동의 서명·변경 요청 (v2.229.0) — 회의록일 때만. ⚠옛 서버(배포 도중)는 안 준다 → optional.
   */
  meetingSign?: MeetingSignDetail | null;
  /**
   * 분기 재고 실사 보고서 본문 (v2.224.0) — docType 이 '분기재고실사보고서' 일 때만. 품목 전체는 확정본 PDF·분기 실사 탭.
   * ⚠옛 서버(배포 도중)는 이 칸을 안 준다 → optional.
   */
  stockAudit?: StockAuditReportBody | null;
  /**
   * 부서별 월간보고서 본문 (v2.280.0) — docType 이 '부서월간보고서' 일 때만. ⚠옛 서버(배포 도중)는 안 준다 → optional.
   */
  deptReport?: DeptReportBody | null;
  /**
   * 처리 이력 (v2.99.0) — 제출·승인·반려·회수를 누가 언제 왜. 오래된 것부터.
   * ⭐문서의 반려 사유는 재제출하면 지워지지만 여기에는 남는다.
   */
  history: {
    action: '제출' | '승인' | '반려' | '회수' | '서명요청' | '서명' | '서명취소' | '내용수정' | '서명완료'
      | '확정본재생성' | '송금완료' | '송금일수정' | '송금취소';
    stage: string | null;
    actorName: string;
    reason: string | null;
    detail: Record<string, unknown> | null;
    at: string;
  }[];
  /**
   * purchaseId = 이 행이 만들어 낸(또는 이어 붙인) 매입 (v2.132.0 · 판매물품 연동). null = 아직 안 감
   * purchaseLink = created(새로 만듦) / matched(이미 있던 매입에 이음) · salesLabel = 「2026-SM-0249 ○○사업」 (v2.246.0)
   * ⚠옛 서버(배포 도중)는 v2.246.0 칸을 안 준다 → optional.
   */
  items: (Required<Pick<ExpenseItem, 'amount'>> & ExpenseItem & {
    seq: number;
    purchaseId?: number | null;
    purchaseLink?: 'created' | 'matched' | null;
    salesLabel?: string | null;
    /** 고른 품목의 이름·그룹 (v2.304.0) — 직접 입력이면 newItem* 에 있다 */
    itemName?: string | null;
    itemGroup?: string | null;
  })[];
  cells: ExpenseLineCell[];
  attachmentId: number | null;
  /**
   * 작성자에게 배정된 차량 (v2.93.0) — 법인카드 작성 화면의 사용대상·비고 기본값.
   * 서버가 **문서 작성자 기준**으로 준다. 배정이 없으면 null.
   */
  authorVehicle: { id: number; label: string; status: string } | null;
  /**
   * 법인카드 — 같은 사용처의 지난 기록으로 채울 값 (v2.227.0) · 열쇠 = 행의 사용처 원래 글자.
   * ⚠옛 서버(배포 도중)는 이 칸을 안 준다 → optional.
   */
  cardVendorHints?: Record<string, VendorHint>;
  receipts: { id: number; filename: string; mime_type: string; file_size: number; uploaded_at: string }[];
  canEdit: boolean;
  canSubmit: boolean;
  canDelete: boolean;
  canApprove: boolean;
  canReject: boolean;
  canRecall: boolean;
  /** 💸 송금완료를 표시한 사람 (v2.267.0) · ⚠옛 서버는 안 준다 */
  remitByName?: string | null;
  /** 💸 송금완료를 표시·고치·풀 수 있나 — 경영지원 지정 계정·admin · 최종승인된 구입지급·개인경비만 (v2.267.0) */
  canMarkRemit?: boolean;
}

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init });
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(apiErrorText(json.error));   // 어느 칸이 왜 틀렸는지까지 (v2.228.1)
  return json.data;
}

export async function listExpenseDocs(params: {
  /**
   * 어느 묶음인가 — 'spending'(기본, 지출결재 3종) / 'holiday'(휴일근무일지) /
   * 'tsp'(기술지원팀 월간보고서, v2.113.0) / 'sales'(영업팀 월간보고서, v2.115.0) /
   * **'all'(전부 — [내 결재함])**. v2.97.0 · all 은 v2.97.1.
   * ⚠결재함처럼 **종류를 가리지 않는 화면**은 반드시 'all' 을 줘야 한다. 안 주면 기본값이
   *   지출 3종만 내려 나머지가 통째로 빠진다(2026-08-26 실제 신고).
   */
  //   'approval'(v2.229.0) = 결재를 도는 종류 전부 — [내 결재함]. 회의록(동의 서명)은 빠진다.
  //   'monthly'(v2.280.0) = 월간보고서 세 종류(기술지원팀·영업팀·부서별) — [부서별 월간보고서] 탭.
  group?: 'spending' | 'holiday' | 'tsp' | 'sales' | 'monthly' | 'meeting' | 'stockAudit' | 'approval' | 'all';
  type?: ExpenseDocType;
  /**
   * 고른 종류만 (v2.134.0) — **더하기(허용 목록)**. [지출결재 문서] 탭이 법인카드를 빼는 데 쓴다.
   * ⛔「빼기」로 적지 말 것 — 문서 종류가 늘면 새 종류가 조용히 섞인다.
   */
  types?: ExpenseDocType[];
  status?: ExpenseStatus;
  year?: number;
  account?: string;
  author?: string;
  q?: string;
  mine?: boolean;
  notFinal?: boolean;
  final?: boolean;
  /** 내 서명을 기다리는 회의록만 (v2.229.0) */
  signMine?: boolean;
  /** 💸 송금 대기만(pending) / 송금완료만(done) (v2.267.0) */
  remit?: 'pending' | 'done';
} = {}): Promise<{
  rows: ExpenseDocListRow[];
  counts: Record<string, number>;
  mineCount: number;
  /**
   * 💸 송금 대기 건수 — 최종승인 · 송금일 없음 · 「이전 문서」 제외 (v2.267.0 · 옛 서버는 안 준다).
   * null = 이 묶음은 안 셌다(지출결재 문서 탭이 아님 · v2.268.0). 숫자는 지금 거르기를 따르고 연도만 무관.
   */
  remitPendingCount?: number | null;
  /** 💸 보는 사람이 송금완료를 표시할 수 있나 — 경영지원 지정 계정·admin (v2.267.0) */
  canMarkRemit?: boolean;
  isApprover: boolean;
  /** 보는 사람이 대표이사(또는 대신하는 사람)인가 — 회의록 탭이 남의 서명 중 회의록도 보여 준다 (v2.229.2) */
  viewerIsCeo?: boolean;
  /** 목록이 상한(500)에서 잘렸는가 — 잘렸으면 화면에서 더한 합계를 믿으면 안 된다 */
  truncated: boolean;
}> {
  const qs = new URLSearchParams();
  if (params.group) qs.set('group', params.group);
  if (params.type) qs.set('type', params.type);
  if (params.types?.length) qs.set('types', params.types.join(','));
  if (params.status) qs.set('status', params.status);
  if (params.year) qs.set('year', String(params.year));
  if (params.account) qs.set('account', params.account);
  if (params.author) qs.set('author', params.author);
  if (params.q) qs.set('q', params.q);
  if (params.mine) qs.set('mine', '1');
  if (params.notFinal) qs.set('notFinal', '1');
  if (params.final) qs.set('final', '1');
  if (params.signMine) qs.set('signMine', '1');
  if (params.remit) qs.set('remit', params.remit);
  return call(`${BASE}/expense/docs?${qs.toString()}`);
}

/**
 * 💸 문서 하나의 송금 상태 (v2.268.0) — **서버가 정한다**(backend lib/expense/remit.ts remitStateOf).
 *   none 송금 없는 종류 · waiting 결재 중 · pending 송금 대기 · done 송금완료 · legacy 이 기능 전에 결재가 끝난 「이전 문서」
 * ⛔화면에서 status·remitDate·remitLegacy 로 다시 판정하지 말 것 — v2.267.0 은 그 판정이 네 곳에 흩어져 있었다.
 */
export type RemitState = 'none' | 'waiting' | 'pending' | 'done' | 'legacy';

/** 💸 송금완료 표시·고치기(remitDate) · 풀기(null) — 경영지원 지정 계정·admin (v2.267.0) */
export async function setExpenseRemit(
  id: number, remitDate: string | null,
): Promise<{ id: number; remitDate: string | null; changed: boolean }> {
  return call(`${BASE}/expense/docs/${id}/remit`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ remitDate }),
  });
}

/**
 * 💸 여러 건 한 번에 송금완료 (v2.268.0) — 송금 대기 목록에서 고른 문서에 같은 송금일.
 * 문서마다 따로 처리한다: done 표시함 · unchanged 이미 그 날짜 · skipped 못 함(이유 · 이미 다른 날짜로 완료인 것 포함).
 */
export async function setExpenseRemitBulk(ids: number[], remitDate: string): Promise<{
  remitDate: string;
  done: { id: number; docNo: string }[];
  unchanged: { id: number; docNo: string }[];
  skipped: { id: number; docNo: string | null; why: string }[];
}> {
  return call(`${BASE}/expense/docs/remit-bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, remitDate }),
  });
}

export async function getExpenseDoc(id: number): Promise<ExpenseDocDetail> {
  return call(`${BASE}/expense/docs/${id}`);
}

/* ── 지출 통계 (v2.53.0) ───────────────────────────── */

export interface ExpenseStats {
  year: number;
  /** 서버가 계산한 내 시야 — all=전사 / dept=내 부서 / self=내 지출만 */
  scope: { level: 'all' | 'dept' | 'self'; depts: string[] };
  /** ⭐통계는 법인카드+개인경비만 (v2.84.0) — byType 에 구입지급결의서 키가 없다 */
  months: { m: number; byType: Partial<Record<ExpenseDocType, number>>; total: number }[];
  summary: { yearTotal: number; docCount: number; pendingCount: number; pendingAmount: number };
  /** 계정과목별 (구입지급결의서는 v2.84.0 부터 통계 제외 — 옛 '구입·지급' 축 없음) */
  byAccount: { account: string; amount: number; count: number }[];
  bySubAccount: { account: string; subAccount: string; amount: number; count: number }[];
  byUser: { userId: string; name: string; dept: string | null; docCount: number; amount: number }[];
  byVendor: { vendor: string; count: number; amount: number }[];
  /** 사용대상별 (v2.54.0) — 접대비=거래처 · 차량유지비=차량 · 복리후생비=직원(1/N 배분) · 여비교통비=목적지 */
  byTarget: { account: string; target: string; amount: number; count: number }[];
  /**
   * 차량별 (v2.61.0) — 차량유지비를 **등록된 차량**으로 묶은 것.
   * 사용대상 칸은 자유 입력이라 같은 차가 여러 표기로 갈라진다 → 차량 기본정보로 매칭한다.
   * unmatched = 어느 차에도 안 붙은 표기. **비어 있어야 정상**이고, 차 있으면 화면이 알린다.
   */
  byVehicle: {
    rows: {
      vehicleId: number | null; name: string; model: string; status: string;
      amount: number; count: number; rawTargets: string[];
    }[];
    unmatched: { target: string; amount: number; count: number; reason: string }[];
  };
}

export async function getExpenseStats(params: {
  year?: number;
  month?: number;
  type?: ExpenseDocType;
  dept?: string;
  author?: string;
  account?: string;
  includePending?: boolean;
} = {}): Promise<ExpenseStats> {
  const qs = new URLSearchParams();
  if (params.year) qs.set('year', String(params.year));
  if (params.month) qs.set('month', String(params.month));
  if (params.type) qs.set('type', params.type);
  if (params.dept) qs.set('dept', params.dept);
  if (params.author) qs.set('author', params.author);
  if (params.account) qs.set('account', params.account);
  if (params.includePending) qs.set('includePending', '1');
  return call(`${BASE}/expense/stats?${qs.toString()}`);
}

/* ── 지출 상세 탐색 — 드릴다운 (v2.63.0) ─────────────
 * 통계에서 계정과목·사용자 줄을 클릭해 점점 좁혀 들어가는 탐색 창의 데이터.
 * account 는 화면의 가상 과목('미지정')도 그대로 보낸다 (옛 '구입·지급' 축은 v2.84.0 통계 제외로 소멸).
 * subAccount='(없음)' / vendor='(미기재)' = 그 칸이 빈 행. */

export interface ExpenseDrillParams {
  year: number;
  month?: number;
  type?: ExpenseDocType;
  dept?: string;
  author?: string;
  account?: string;
  subAccount?: string;
  vendor?: string;
  /**
   * 사용대상 (v2.64.0) — 차량처럼 한 대상이 여러 표기로 갈라진 경우 표기 전부를 보낸다.
   * 복리후생비는 서버가 인원수로 1/N 배분한 몫만 집계한다 (통계 화면과 같은 규칙).
   */
  usageTargets?: string[];
  includePending?: boolean;
}

export interface ExpenseDrillItem {
  docId: number;
  docNo: string;
  docType: ExpenseDocType;
  status: ExpenseStatus;
  authorId: string;
  authorName: string;
  /** 사용일 (구입지급은 작성일) — 집계 날짜 축과 같은 값 */
  itemDate: string | null;
  account: string;
  subAccount: string | null;
  usageTarget: string | null;
  vendor: string | null;
  description: string | null;
  /** 이 조회에 잡히는 금액 — 복리후생비 1/N 이면 그 사람 몫만 */
  amount: number;
  /** 행 원금액 (1/N 이전) */
  fullAmount: number;
  /** 함께 쓴 인원 수 — 1보다 크면 amount 는 1인분 */
  shareParts: number;
  note: string | null;
}

export interface ExpenseDrill {
  year: number;
  summary: { amount: number; itemCount: number; docCount: number };
  byMonth: { m: number; amount: number; count: number }[];
  byAccount: { account: string; amount: number; count: number }[];
  bySubAccount: { subAccount: string; amount: number; count: number }[];
  byUser: {
    userId: string; name: string; dept: string | null;
    docCount: number; amount: number; count: number;
  }[];
  /** 상위 30곳까지만 — vendorKinds 가 실제 가짓수 */
  byVendor: { vendor: string; amount: number; count: number }[];
  vendorKinds: number;
  /**
   * 사용대상별 (v2.64.0) — 계정과목 하나를 고른 상태에서만 채워진다.
   * 차량유지비는 등록 차량으로 묶여 오고(rawTargets = 묶인 원본 표기들), 나머지는 적힌 글자 그대로.
   */
  byUsageTarget: { label: string; sub: string; rawTargets: string[]; amount: number; count: number }[];
  /** 지금 걸린 사용대상 표기들 */
  usageTargets: string[];
  /** 1/N 배분이 적용된 조회인가 — 화면이 안내문을 띄운다 */
  allocated: boolean;
  items: ExpenseDrillItem[];
  /** 내역이 300건 상한에서 잘렸는가 — 잘렸으면 화면이 알린다 */
  itemsTruncated: boolean;
}

export async function getExpenseDrill(params: ExpenseDrillParams): Promise<ExpenseDrill> {
  const qs = new URLSearchParams();
  qs.set('year', String(params.year));
  if (params.month) qs.set('month', String(params.month));
  if (params.type) qs.set('type', params.type);
  if (params.dept) qs.set('dept', params.dept);
  if (params.author) qs.set('author', params.author);
  if (params.account) qs.set('account', params.account);
  if (params.subAccount) qs.set('subAccount', params.subAccount);
  if (params.vendor) qs.set('vendor', params.vendor);
  // 사용대상은 여러 개 — 같은 이름으로 반복해 붙인다 (차량 한 대 = 표기 여러 개)
  for (const t of params.usageTargets ?? []) qs.append('usageTarget', t);
  if (params.includePending) qs.set('includePending', '1');
  return call(`${BASE}/expense/stats/drill?${qs.toString()}`);
}

export async function createExpenseDoc(
  body: ExpenseDocFields & { docType: ExpenseDocType; statementId?: number | null },
): Promise<{ id: number; docNo: string; status: ExpenseStatus }> {
  return call(`${BASE}/expense/docs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateExpenseDoc(
  id: number,
  body: ExpenseDocFields,
): Promise<{ id: number; docNo: string; status: ExpenseStatus }> {
  return call(`${BASE}/expense/docs/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function deleteExpenseDoc(id: number): Promise<void> {
  await call(`${BASE}/expense/docs/${id}`, { method: 'DELETE' });
}

/**
 * 제출 전 미리보기 PDF ([확인] 단계 — v2.51.0 구입지급 → v2.52.0 3종 공통).
 * 저장 없이 실제 제출본과 같은 렌더러로 만든 PDF Blob URL 반환 (호출부가 팝업으로 연다).
 * statementId 를 주면 카드 사용기간(귀속월 1일~말일)도 제출본과 똑같이 맞춰 준다.
 */
export async function previewExpensePdf(
  body: ExpenseDocFields & {
    docType: ExpenseDocType;
    statementId?: number | null;
    /** 고치는 중인 문서 번호 — 그 문서의 첨부까지 제출본과 똑같이 붙여 준다 (v2.73.0) */
    previewDocId?: number | null;
  },
): Promise<string> {
  const res = await fetch(`${BASE}/expense/docs/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(json?.error?.message ?? '미리보기 생성 실패');
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

async function docAction(
  id: number,
  action: 'approve' | 'reject' | 'recall',
  body?: { reason?: string },
): Promise<{
  id: number; status: ExpenseStatus; regenerated: boolean;
  /** 최종승인 때 이어 준 결과 한 줄 — 「매입관리에 2줄 등록 …」 (승인만 · v2.246.0) */
  followUp?: string | null;
  /** 최종승인 회수 때 남겨 둔 매입과 이유 (회수만 · v2.246.0) */
  purchaseKept?: { purchaseId: number; why: string }[];
}> {
  return call(`${BASE}/expense/docs/${id}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

export const approveExpenseDoc = (id: number) => docAction(id, 'approve');

/** 매입으로 못 보낸 줄 — 이유와 함께 (v2.246.0) */
export interface HeldLine { seq: number; why: string; candidates?: number[] }

/**
 * 판매물품 지출결의서 → 매입관리 연동 (v2.132.0 · v2.246.0).
 * v2.246.0: 최종승인 때 서버가 자동으로 한다 — 이 단추는 자동이 못 한 줄을 다시 시도하는 길.
 * 내역 줄마다 매입 1행(고른 영업건 · 비면 재고 보유)을 만들거나 이미 있던 같은 매입에 잇는다. 이미 간 줄은 건너뛴다.
 * @param force 같은 물품으로 보이는 매입이 여럿이어도 새로 만든다
 */
export function linkExpensePurchases(
  id: number, force = false, companyId: 'SM' | 'EJ' = 'SM',
): Promise<{ created: number; needsReview?: number; matched?: number; skipped: number; purchaseIds: number[]; held?: HeldLine[]; summary?: string }> {
  return call(`${BASE}/expense/docs/${id}/link-purchases`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, force }),
  });
}

/** 매입 연동 해제 — 손대지 않은 매입만 지우고, 이어 붙인 매입은 우리가 바꾼 칸만 되돌리고 연결을 푼다 */
export function unlinkExpensePurchases(
  id: number,
): Promise<{ removed: number; restored?: number; kept: { purchaseId: number; why: string }[] }> {
  return call(`${BASE}/expense/docs/${id}/link-purchases`, { method: 'DELETE' });
}
export const rejectExpenseDoc = (id: number, reason?: string) => docAction(id, 'reject', { reason });
/** 승인 회수 — ⭐완결(최종승인) 문서는 사유 필수 (v2.101.0, 서버가 거절한다) */
export const recallExpenseDoc = (id: number, reason?: string) =>
  docAction(id, 'recall', reason ? { reason } : undefined);

/* ── 법인카드 사용내역 업로드 ───────────────────────── */

export interface CardStatementRow {
  id: number;
  targetUser: string;
  targetName: string;
  yearMonth: string;
  cardNo: string | null;
  origFilename: string;
  rowCount: number;
  uploadedBy: string;
  /** 업로더 이름 (직원 기록이 없으면 계정 아이디) — v2.56.0 */
  uploaderName: string;
  uploadedAt: string;
  /** 업로드 원본 엑셀이 보관돼 있는지 (v2.56.0 이전 업로드분은 false) */
  hasFile: boolean;
  /** 삭제 가능 — 올린 본인·admin, 그리고 아직 문서가 안 만들어진 건만 */
  canDelete: boolean;
  /** 딸린 문서까지 함께 삭제 가능 (v2.109.0) — 잘못 올려 작성중 문서가 딸린 건 정리용.
   *  최종승인 문서가 붙어 있으면 false (그건 여전히 못 지운다). */
  canDeleteWithDoc: boolean;
  docId: number | null;
  docNo: string | null;
  docStatus: ExpenseStatus | null;
  /** 연결 문서가 소급 등록이면 그 안내글 (v2.57.1). null = 포털에서 결재가 돈 일반 건 */
  backfillNote: string | null;
}

export async function listCardStatements(): Promise<{
  rows: CardStatementRow[];
  canUpload: boolean;
  /** 남의 명세서가 보이는 사람인가 — 경영지원·admin·대표이사·부서장 (v2.134.0) */
  seeOthers: boolean;
}> {
  return call(`${BASE}/expense/card-statements`);
}

/* ── 차량 기본정보 (v2.61.0) ───────────────────────── */

export const VEHICLE_STATUSES = ['사용중', '계약만료'] as const;
export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];

export interface VehicleRow {
  id: number;
  /** 화면에 보이는 이름 (= 차량번호, 번호 없는 개인차량은 그 이름) */
  name: string;
  /** 매칭용 차량번호. 비면 이름·별칭으로만 찾는다 */
  plateNo: string;
  model: string;
  status: VehicleStatus;
  /** 지출내역에 실제로 적히는 표기 변형 (「6526」 처럼) */
  aliases: string[];
  sortOrder: number;
  note: string;
  /** 담당 직원 (v2.93.0) — 법인카드 작성 때 사용대상·비고 자동 입력의 근거. 한 직원 = 한 대 */
  assignedEmpId: string | null;
  /** 이 차에 붙은 지출내역 (최종승인·전 기간) — 금액은 경영지원·대표·admin 외 null (v2.84.0) */
  matchedCount: number;
  matchedAmount: number | null;
  matchedTargets: string[];
}

export interface VehicleInput {
  name: string;
  plateNo: string;
  model: string;
  status: VehicleStatus;
  aliases: string[];
  sortOrder: number;
  note: string;
  assignedEmpId: string | null;
}

export async function listVehicles(): Promise<{
  canEdit: boolean;
  /** 매칭 금액 열람 권한 (경영지원·대표·admin) — false 면 rows/unmatched 의 금액이 null (v2.84.0) */
  canViewAmounts: boolean;
  rows: VehicleRow[];
  unmatched: { target: string; amount: number | null; count: number; reason: string }[];
}> {
  return call(`${BASE}/expense/vehicles`);
}

export async function createVehicle(body: VehicleInput): Promise<{ id: number }> {
  return call(`${BASE}/expense/vehicles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateVehicle(id: number, body: VehicleInput): Promise<{ id: number }> {
  return call(`${BASE}/expense/vehicles/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function deleteVehicle(id: number): Promise<void> {
  await call(`${BASE}/expense/vehicles/${id}`, { method: 'DELETE' });
}

export async function uploadCardStatement(params: {
  file: File;
  targetUser: string;
  yearMonth?: string;
}): Promise<{ id: number; rowCount: number; cardNo: string | null; yearMonth: string }> {
  const fd = new FormData();
  fd.append('targetUser', params.targetUser);
  if (params.yearMonth) fd.append('yearMonth', params.yearMonth);
  fd.append('file', params.file);
  const res = await fetch(`${BASE}/expense/card-statements`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = (await res.json()) as ApiResponse<{
    id: number; rowCount: number; cardNo: string | null; yearMonth: string;
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function deleteCardStatement(id: number, withDocs = false): Promise<void> {
  // withDocs = 딸린 작성중 문서까지 함께 (화면이 한 번 더 물어본 뒤에만 붙인다)
  const qs = withDocs ? '?withDocs=1' : '';
  await call(`${BASE}/expense/card-statements/${id}${qs}`, { method: 'DELETE' });
}

/** 업로드 원본 엑셀 다시 받기 (v2.56.0) — 서버가 보낸 파일명 그대로 저장 */
export async function downloadCardStatementFile(id: number, fallbackName: string): Promise<void> {
  const res = await fetch(`${BASE}/expense/card-statements/${id}/file`, { credentials: 'include' });
  if (!res.ok) {
    // 실패는 JSON 에러 본문으로 온다 (파일 미보관·권한 없음 등)
    let msg = '파일을 받지 못했습니다';
    try {
      const j = (await res.json()) as ApiResponse<unknown>;
      if (!j.ok) msg = j.error.message;
    } catch { /* 본문이 JSON 이 아니면 기본 메시지 */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fallbackName || `카드사용내역_${id}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * 문서 1건을 엑셀로 (v2.111.0) — 법인카드사용내역서 전용.
 * 결재판·서명은 없고 **작성된 내용만** 담긴다(2026-09-01 사용자 지시).
 * ⚠plain <a href> 로 열면 오류(세션 만료 등)일 때 JSON 페이지로 이탈한다 — fetch+blob 으로 받는다.
 */
export async function downloadExpenseDocXlsx(id: number, docNo: string): Promise<void> {
  const res = await fetch(`${BASE}/expense/docs/${id}/export.xlsx`, { credentials: 'include' });
  if (!res.ok) {
    let msg = `엑셀을 받지 못했습니다 (${res.status})`;
    try {
      const j = (await res.json()) as ApiResponse<unknown>;
      if (!j.ok) msg = j.error.message;
    } catch { /* 본문이 JSON 이 아니면 기본 메시지 */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${docNo}_법인카드사용내역서.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ── 계정 목록 (카드내역 대상자 지정·필터) ───────────── */

export interface PortalUser {
  userId: string;
  name: string;
  department: string | null;
  title: string | null;
}

export async function listPortalUsers(): Promise<PortalUser[]> {
  const data = await call<{ users: PortalUser[] }>(`${BASE}/expense/users`);
  return data.users;
}

/* ── 지출문서 첨부 (영수증·견적서) ───────────────────
 *
 * ⭐문서종류별 첨부 종류는 **서버 lib/approval/expense.ts 의 EXPENSE_ATTACH_KIND 와 같은 표**다.
 *   여기 따로 두는 이유: 새 문서는 아직 서버에 없어서 응답으로 받을 수가 없다.
 *   ⚠한쪽만 고치면 업로드가 서버에서 거절된다 — 늘 같이 고칠 것.
 *
 * ⭐표의 열쇠는 **ApprovalDocType**(사내결재를 도는 문서 전부)이다 — v2.119.0 에 넓혔다.
 *   전에는 지출 3종만 적어 두었는데, 회의록에 첨부를 붙이면서 **「여기 없는 종류」가
 *   조용히 첨부 없는 문서가 되는** 모양이 됐다. 이제 종류를 늘리면 컴파일러가
 *   「이 문서는 첨부를 받나」를 반드시 적게 만든다(null = 안 받음).
 *   ⚠null 인 종류로 첨부 상자를 그리려 하지 말 것 — 서버가 거절한다.
 *   ⚠**kind 가 있다고 화면에서 다 올릴 수 있는 것은 아니다** — 기술지원팀월간보고서는
 *     kind 가 있어도 기술지원 포털만 넣는다. 상자를 그릴지는 화면이 따로 정한다.
 */

export const EXPENSE_ATTACH_KIND: Record<ApprovalDocType, string | null> = {
  구입지급결의서: '구입견적서',
  개인경비정산서: '지출증빙',
  법인카드사용내역서: null,
  // 근무 사실을 적는 문서라 사람이 올리는 증빙이 없다
  휴일근무일지: null,
  /**
   * 보고서 PDF 자체가 알맹이다. 서버 표와 **같은 문자열**을 적어 둔다(값이 갈리면 안 된다).
   * ⚠단 **넣는 것은 기술지원 포털뿐** — 화면에서 올리면 서버가 거절한다(v2.113.0).
   *   그래서 이 종류로는 첨부 상자를 그리지 않는다.
   */
  기술지원팀월간보고서: '월간보고서',
  // 본문을 포털이 그달 자료로 만든다 — 사람이 붙이는 파일이 없다 (v2.115.0)
  영업팀월간보고서: null,
  // 회의 자료 파일 (v2.119.0). 서버 EXPENSE_ATTACH_KIND 와 같은 문자열이어야 한다
  회의록: '회의자료',
  // 본문을 실사 기록으로 포털이 만든다 — 사람이 붙이는 파일이 없다 (v2.224.0)
  분기재고실사보고서: null,
  // 본문을 사람이 화면에서 적는다 — 사람이 붙이는 파일이 없다 (v2.280.0)
  부서월간보고서: null,
};

export const EXPENSE_ATTACH_LABEL: Record<ApprovalDocType, string> = {
  구입지급결의서: '견적서',
  개인경비정산서: '영수증·증빙',
  법인카드사용내역서: '첨부',
  휴일근무일지: '첨부',
  기술지원팀월간보고서: '보고서',
  영업팀월간보고서: '첨부',
  회의록: '회의자료',
  분기재고실사보고서: '첨부',
  부서월간보고서: '첨부',
};

export function expenseAttachKind(t: ApprovalDocType): string | null {
  return EXPENSE_ATTACH_KIND[t];
}

/** 파일 고르기 창에서 보여 줄 형식 — 서버 ALLOWED_EXT 와 맞춰 둔다 */
export const ATTACH_ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.xlsx,.xlsm,.xls,.csv';

export async function uploadReceipt(docId: number, file: File, kind: string): Promise<void> {
  const fd = new FormData();
  fd.append('parentKind', 'expense');
  fd.append('parentId', String(docId));
  fd.append('kind', kind);
  fd.append('file', file);
  const res = await fetch(`${BASE}/attachments`, { method: 'POST', credentials: 'include', body: fd });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

export async function deleteReceipt(attachmentId: number): Promise<void> {
  await call(`${BASE}/attachments/${attachmentId}`, { method: 'DELETE' });
}

export function attachmentDownloadUrl(attachmentId: number): string {
  return `${BASE}/attachments/${attachmentId}/download`;
}
